import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  message,
  Popconfirm,
  Spin,
} from 'antd';
import {
  DollarOutlined,
  PlusOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import MainLayout from '../../../layouts/MainLayout';
import { superadminGlobalCurrencySettingsApi, show422Error } from '../../../utils/api';
import { PANEL_PRIMARY_BTN_CLASS } from '../../../constants/ui';
import { TableSkeleton } from '../../../components/SkeletonLoaders';

const CURRENCY_POSITION_OPTIONS = [
  { value: 'left', label: 'چپ' },
  { value: 'right', label: 'راست' },
  { value: 'left_with_space', label: 'چپ با فاصله' },
  { value: 'right_with_space', label: 'راست با فاصله' },
];

const CRYPTO_OPTIONS = [
  { value: 'no', label: 'خیر' },
  { value: 'yes', label: 'بله' },
];

const positionLabel = (v) => CURRENCY_POSITION_OPTIONS.find((o) => o.value === v)?.label || v || '—';

const extractList = (res) => res?.data?.currencies || res?.currencies || [];

const isCryptoYes = (v) => v === 'yes' || v === 1 || v === '1';

const SuperadminGlobalCurrencySettings = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [formOpen, setFormOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const fetchIndex = useCallback(async () => {
    setLoading(true);
    try {
      const res = await superadminGlobalCurrencySettingsApi.list();
      setRows(extractList(res) || []);
    } catch (error) {
      message.error(error?.message || 'خطا در دریافت فهرست ارزها');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchIndex();
  }, [fetchIndex]);

  const buildPayload = (values) => {
    const crypto = isCryptoYes(values.is_cryptocurrency);
    const payload = {
      currency_name: String(values.currency_name || '').trim(),
      currency_symbol: String(values.currency_symbol || '').trim(),
      currency_code: String(values.currency_code || '').trim(),
      is_cryptocurrency: crypto ? 1 : 0,
      exchange_rate: values.exchange_rate != null ? Number(values.exchange_rate) : 0,
      currency_position: values.currency_position,
      no_of_decimal: values.no_of_decimal != null ? Number(values.no_of_decimal) : 0,
      thousand_separator: String(values.thousand_separator ?? ','),
      decimal_separator: String(values.decimal_separator ?? '.'),
    };
    if (crypto && values.usd_price != null && values.usd_price !== '') {
      payload.usd_price = Number(values.usd_price);
    }
    return payload;
  };

  const openCreate = () => {
    setEditingId(null);
    setFormOpen(true);
    setFormLoading(false);
    form.resetFields();
    form.setFieldsValue({
      is_cryptocurrency: 'no',
      exchange_rate: 1,
      no_of_decimal: 2,
      thousand_separator: ',',
      decimal_separator: '.',
      currency_position: 'left',
    });
  };

  const openEdit = async (id) => {
    setEditingId(id);
    setFormOpen(true);
    setFormLoading(true);
    form.resetFields();
    try {
      const res = await superadminGlobalCurrencySettingsApi.getEditMeta(id);
      const c = res?.currency;
      if (!c) {
        message.warning('رکورد یافت نشد');
        setFormOpen(false);
        return;
      }
      form.setFieldsValue({
        currency_name: c.currency_name,
        currency_symbol: c.currency_symbol,
        currency_code: c.currency_code,
        is_cryptocurrency: isCryptoYes(c.is_cryptocurrency) ? 'yes' : 'no',
        exchange_rate: c.exchange_rate,
        usd_price: c.usd_price,
        currency_position: c.currency_position,
        no_of_decimal: c.no_of_decimal,
        thousand_separator: c.thousand_separator ?? ',',
        decimal_separator: c.decimal_separator ?? '.',
      });
    } catch (error) {
      message.error(error?.message || 'خطا در دریافت اطلاعات ارز');
      setFormOpen(false);
    } finally {
      setFormLoading(false);
    }
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = buildPayload(values);
      setFormSubmitting(true);
      if (editingId) {
        await superadminGlobalCurrencySettingsApi.update(editingId, payload);
        message.success('ارز بروزرسانی شد');
      } else {
        await superadminGlobalCurrencySettingsApi.create(payload);
        message.success('ارز جدید ثبت شد');
      }
      setFormOpen(false);
      fetchIndex();
    } catch (error) {
      if (error?.errorFields) return;
      if (error?.name === 'ValidationError' || error?.details) show422Error(error, form);
      else message.error(error?.message || 'خطا در ذخیره');
    } finally {
      setFormSubmitting(false);
    }
  };

  const onDelete = async (id) => {
    try {
      await superadminGlobalCurrencySettingsApi.delete(id);
      message.success('ارز حذف شد');
      fetchIndex();
    } catch (error) {
      message.error(error?.message || 'خطا در حذف');
    }
  };

  const columns = [
    { title: 'نام', dataIndex: 'currency_name', key: 'currency_name', ellipsis: true },
    { title: 'نماد', dataIndex: 'currency_symbol', key: 'currency_symbol', width: 90 },
    { title: 'کد', dataIndex: 'currency_code', key: 'currency_code', width: 100 },
    {
      title: 'نرخ برابری',
      dataIndex: 'exchange_rate',
      key: 'exchange_rate',
      width: 110,
      render: (v) => (v != null ? String(v) : '—'),
    },
    {
      title: 'رمزارز',
      dataIndex: 'is_cryptocurrency',
      key: 'crypto',
      width: 90,
      render: (v) => (
        <Tag color={isCryptoYes(v) ? 'purple' : 'default'}>{isCryptoYes(v) ? 'بله' : 'خیر'}</Tag>
      ),
    },
    {
      title: 'وضعیت',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (v) => (
        <Tag color={v === 'enable' ? 'green' : 'default'}>{v === 'enable' ? 'فعال' : 'غیرفعال'}</Tag>
      ),
    },
    {
      title: 'موقعیت نمایش',
      dataIndex: 'currency_position',
      key: 'currency_position',
      ellipsis: true,
      render: (v) => positionLabel(v),
    },
    {
      title: 'عملیات',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, row) => (
        <Space size="small">
          <Button type="text" icon={<EditOutlined />} aria-label="ویرایش" onClick={() => openEdit(row.id)} />
          <Popconfirm
            title="حذف این ارز؟"
            okText="حذف"
            cancelText="لغو"
            onConfirm={() => onDelete(row.id)}
            okButtonProps={{ danger: true }}
          >
            <Button type="text" danger icon={<DeleteOutlined />} aria-label="حذف" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="custom-font w-full min-w-0" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" styles={{ body: { padding: 0 } }}>
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 bg-white p-4 sm:p-6 dark:border-gray-700 dark:bg-gray-800 rounded-t-xl">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-11 w-11 sm:h-12 sm:w-12 flex-shrink-0 items-center justify-center rounded-xl bg-emerald-50 dark:bg-emerald-900/25">
                <DollarOutlined className="text-lg sm:text-xl text-emerald-600 dark:text-emerald-400" />
              </div>
              <div className="min-w-0">
                <h1 className="m-0 text-xl sm:text-2xl font-bold text-gray-800 dark:text-gray-100">تنظیمات ارز</h1>
                <p className="m-0 mt-1 text-xs text-gray-500 dark:text-gray-400 sm:text-sm">مدیریت ارزهای سراسری سیستم</p>
              </div>
            </div>
            <Space wrap>
              <Button icon={<ReloadOutlined />} onClick={fetchIndex} loading={loading}>
                بروزرسانی
              </Button>
              <Button type="primary" icon={<PlusOutlined />} className={PANEL_PRIMARY_BTN_CLASS} onClick={openCreate}>
                ارز جدید
              </Button>
            </Space>
          </div>

          <div className="rounded-b-xl bg-gray-50 p-4 sm:p-6 dark:bg-gray-900/50">
            <Card className="border border-gray-100 shadow-sm dark:border-gray-700 dark:bg-gray-800 overflow-hidden">
              {loading ? (
                <div className="p-4 sm:p-5">
                  <TableSkeleton rows={6} columns={7} />
                </div>
              ) : (
                <Table
                  rowKey="id"
                  loading={false}
                  columns={columns}
                  dataSource={rows}
                  pagination={false}
                  scroll={{ x: 'max-content' }}
                />
              )}
            </Card>
          </div>
        </Card>

        <Modal
          wrapClassName="custom-font"
          title={editingId ? 'ویرایش ارز' : 'افزودن ارز'}
          open={formOpen}
          onCancel={() => setFormOpen(false)}
          onOk={onSubmit}
          okText="ذخیره"
          cancelText="لغو"
          confirmLoading={formSubmitting}
          okButtonProps={{ className: PANEL_PRIMARY_BTN_CLASS }}
          destroyOnClose
          width={640}
        >
          <Form form={form} layout="vertical" className="custom-font mt-2" disabled={formLoading}>
            <Spin spinning={formLoading}>
              <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
                <Form.Item name="currency_name" label="نام ارز" rules={[{ required: true, message: 'نام را وارد کنید' }]}>
                  <Input placeholder="مثال: تومان" />
                </Form.Item>
                <Form.Item name="currency_symbol" label="نماد" rules={[{ required: true, message: 'نماد را وارد کنید' }]}>
                  <Input placeholder="مثال: تومان" />
                </Form.Item>
                <Form.Item name="currency_code" label="کد ارز" rules={[{ required: true, message: 'کد را وارد کنید' }]}>
                  <Input dir="ltr" placeholder="IRR، USD، …" />
                </Form.Item>
                <Form.Item name="is_cryptocurrency" label="رمزارز" rules={[{ required: true }]}>
                  <Select options={CRYPTO_OPTIONS} popupClassName="custom-font" />
                </Form.Item>
                <Form.Item name="exchange_rate" label="نرخ برابری" rules={[{ required: true, message: 'نرخ را وارد کنید' }]}>
                  <InputNumber min={0} step={0.000001} className="w-full" />
                </Form.Item>
                <Form.Item noStyle shouldUpdate={(prev, cur) => prev.is_cryptocurrency !== cur.is_cryptocurrency}>
                  {({ getFieldValue }) =>
                    isCryptoYes(getFieldValue('is_cryptocurrency')) ? (
                      <Form.Item
                        name="usd_price"
                        label="قیمت دلاری (USD)"
                        rules={[{ required: true, message: 'قیمت دلاری را وارد کنید' }]}
                      >
                        <InputNumber min={0} step={0.01} className="w-full" />
                      </Form.Item>
                    ) : null
                  }
                </Form.Item>
                <Form.Item name="currency_position" label="موقعیت نمایش مبلغ" rules={[{ required: true }]}>
                  <Select options={CURRENCY_POSITION_OPTIONS} popupClassName="custom-font" />
                </Form.Item>
                <Form.Item name="no_of_decimal" label="تعداد رقم اعشار" rules={[{ required: true }]}>
                  <InputNumber min={0} max={10} className="w-full" />
                </Form.Item>
                <Form.Item name="thousand_separator" label="جداکننده هزارگان" rules={[{ required: true }]}>
                  <Input maxLength={4} placeholder="," />
                </Form.Item>
                <Form.Item name="decimal_separator" label="جداکننده اعشار" rules={[{ required: true }]}>
                  <Input maxLength={4} placeholder="." />
                </Form.Item>
              </div>
            </Spin>
          </Form>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default SuperadminGlobalCurrencySettings;
